 
import {
  createI18n,
  type Composer,
  type I18n,
} from 'vue-i18n';

// these imports are generated by i18-bundle-plugin.ts
import en from '@/locales/en.json';
import de from '@/locales/de.json';

import {loggerInstance} from '@/ts/instances/logger-instance';
import {persistentStorage} from '@/ts/instances/persistant-storage';
import {Language} from '@/ts/types/component/localization.type';
import {MULTI_LANGUAGE, LANGUAGE} from '@/ts/utils/consts';
import {getCurrentUrlLanguage} from '@/ts/composables/stateful/use-i18n-router';
import {detectBrowserLanguage} from '@/ts/utils/browser-language';
import {analyticsService} from '@/ts/services/analytics-service';
import {VoucherCodeQuery, getLanguageForVoucherCode} from '@/ts/types/component/sign-up-type';
import {SUPPORTED_LANGUAGES} from '@/ts/composables/stateful/use-lang';


function getFallbackLocale(logger: ReturnType<typeof loggerInstance.getLogger>, savedLang: Language | null): Language {
  // Check for voucher code before saved language
  const voucherLang = getLanguageForVoucherCode(VoucherCodeQuery.ALLIANZ);
  if (voucherLang) {
    logger.debug(`Using language for voucher code: ${voucherLang}`)();
    return voucherLang;
  }

  if (savedLang && SUPPORTED_LANGUAGES.includes(savedLang)) {
    logger.debug(`Restoring language setting from user cache to ${savedLang}`)();
    return savedLang;
  }

  // The browser's language when Balm speaks it, and the deployment's own otherwise.
  const browserLang = detectBrowserLanguage();
  logger.debug(`Using ${browserLang}, from the browser or the deployment's default`)();
  return browserLang;
}

function determineLocale(logger: ReturnType<typeof loggerInstance.getLogger>): Language {
  const savedLang = persistentStorage.language;
  let locale: Language = LANGUAGE as Language;

  if (MULTI_LANGUAGE) {
    const urlLang = getCurrentUrlLanguage();
    if (urlLang) {
      locale = urlLang;
      logger.debug(`Using language from URL: ${locale}`)();
      if (savedLang !== locale) {
        persistentStorage.language = locale;
      }
    } else {
      locale = getFallbackLocale(logger, savedLang);
    }
  } else {
    logger.debug(`Multi language is off, forcing default language ${locale}`)();
  }

  return locale;
}

let instance: I18n | null = null;

function createInstance(): I18n {
  const logger = loggerInstance.getLogger('i18n');
  const messages = {en, de};
  logger.debug('Created translations en:', messages)();

  const locale = determineLocale(logger);
  analyticsService.setLanguage(locale);
  logger.debug(`Set user Locale to ${locale}`)();

  return createI18n({
    legacy: false,
    globalInjection: true,
    locale,
    // A key the German bundle misses shows its English text rather than the bare key.
    fallbackLocale: Language.English,
    messages,
  });
}

/** Creates and configures the Vue I18n instance with locale detection from URL, storage, or browser */
export function i18nFactory(): I18n {
  instance ??= createInstance();
  return instance;
}

/** Translation for code outside a component, such as the router's document-title hook. */
export function translate(key: string): string {
  return (i18nFactory().global as Composer).t(key);
}

/** The language the app is rendering in, for the same callers as `translate`. */
export function currentLanguage(): Language {
  return (i18nFactory().global as Composer).locale.value as Language;
}
